import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  TrendingUp,
  Users,
  Award,
  Calendar,
  BookOpen,
  Building2,
  AlertTriangle,
  Download,
  RefreshCw,
  Star,
  BarChart3
} from 'lucide-react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [attendanceTrends, setAttendanceTrends] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [centerAnalytics, setCenterAnalytics] = useState([]);
  const [atRiskStudents, setAtRiskStudents] = useState([]);
  const [teacherPerformance, setTeacherPerformance] = useState([]);
  const user = JSON.parse(localStorage.getItem('user'));
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadAnalyticsData();
  }, []);

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Use new optimized combined endpoint - single API call!
      const response = await axios.get(`${API}/analytics/combined`, { headers });
      const data = response.data;

      // Set all state from single response
      setOverview(data.overview);
      setAttendanceTrends(data.attendance_trends || []);
      setPerformance(data.performance || []);
      setAtRiskStudents(data.at_risk_students || []);

      // Admin-specific data comes in the same response
      if (isAdmin && data.centers) {
        setCenterAnalytics(data.centers);
        setTeacherPerformance(data.teacher_performance || []);
      }

      toast.success('Analytics loaded successfully');
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadAnalyticsData();
  };

  const handleExport = (format) => {
    toast.info(`Exporting to ${format.toUpperCase()}...`);
    // TODO: Implement export functionality
  };

  // Colors for charts
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
  const GRADIENT_COLORS = {
    primary: ['#3b82f6', '#1d4ed8'],
    success: ['#10b981', '#059669'],
    warning: ['#f59e0b', '#d97706']
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Analytics & Insights
          </h2>
          <p className="text-gray-600 text-sm mt-1">Real-time performance metrics and trends</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleRefresh}
            variant="outline"
            className="flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button
            onClick={() => handleExport('pdf')}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export PDF
          </Button>
          <Button
            onClick={() => handleExport('excel')}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-0 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Total Students</p>
                <h3 className="text-3xl font-bold text-blue-900 mt-2">{overview.total_students}</h3>
                <p className="text-xs text-blue-600 mt-1">{overview.active_students} active</p>
              </div>
              <div className="bg-blue-500 p-3 rounded-xl">
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-0 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Attendance Rate</p>
                <h3 className="text-3xl font-bold text-green-900 mt-2">{overview.attendance_rate}%</h3>
                <p className="text-xs text-green-600 mt-1">Last 30 days</p>
              </div>
              <div className="bg-green-500 p-3 rounded-xl">
                <Calendar className="w-6 h-6 text-white" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 border-0 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">Avg Star Rating</p>
                <h3 className="text-3xl font-bold text-purple-900 mt-2">{overview.avg_star_rating}</h3>
                <p className="text-xs text-purple-600 mt-1">Out of 5</p>
              </div>
              <div className="bg-purple-500 p-3 rounded-xl">
                <Star className="w-6 h-6 text-white" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-orange-50 to-orange-100 border-0 hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600 font-medium">Task Completion</p>
                <h3 className="text-3xl font-bold text-orange-900 mt-2">{overview.task_completion_rate}%</h3>
                <p className="text-xs text-orange-600 mt-1">All tasks</p>
              </div>
              <div className="bg-orange-500 p-3 rounded-xl">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Attendance Trends */}
      <Card className="p-6 bg-white border-0 shadow-lg">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Attendance Trends
            </h3>
            <p className="text-sm text-gray-600 mt-1">Last 30 days attendance patterns</p>
          </div>
          <TrendingUp className="w-5 h-5 text-blue-500" />
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={attendanceTrends}>
            <defs>
              <linearGradient id="colorAttendance" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="date" stroke="#6b7280" style={{ fontSize: '12px' }} />
            <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
            <Tooltip
              contentStyle={{
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}
            />
            <Legend />
            <Area
              type="monotone"
              dataKey="attendance_percentage"
              stroke="#3b82f6"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorAttendance)"
              name="Attendance %"
            />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      {/* Performance Overview */}
      {performance && performance.star_distribution && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6 bg-white border-0 shadow-lg">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Star Rating Distribution
                </h3>
                <p className="text-sm text-gray-600 mt-1">Overall performance ratings</p>
              </div>
              <Award className="w-5 h-5 text-yellow-500" />
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={Object.entries(performance.star_distribution || {}).map(([stars, count]) => ({
                    name: `${stars} Star${stars > 1 ? 's' : ''}`,
                    value: count
                  }))}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {Object.entries(performance.star_distribution || {}).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>

          <Card className="p-6 bg-white border-0 shadow-lg">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Top Performers
                </h3>
                <p className="text-sm text-gray-600 mt-1">Top 10 students by average rating</p>
              </div>
              <BarChart3 className="w-5 h-5 text-green-500" />
            </div>
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {(performance.top_performers || []).map((student, index) => (
                <div key={student.student_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                      index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-600' : 'bg-blue-500'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{student.student_name}</p>
                      <p className="text-xs text-gray-500">{student.total_ratings} ratings</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-yellow-500 fill-current" />
                    <span className="font-bold text-gray-900">{student.avg_stars}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Center Comparison (Admin Only) */}
      {isAdmin && centerAnalytics.length > 0 && (
        <Card className="p-6 bg-white border-0 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Center Comparison
              </h3>
              <p className="text-sm text-gray-600 mt-1">Performance across all centers</p>
            </div>
            <Building2 className="w-5 h-5 text-indigo-500" />
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={centerAnalytics}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="center_name" stroke="#6b7280" style={{ fontSize: '12px' }} />
              <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Legend />
              <Bar dataKey="student_count" fill="#3b82f6" name="Students" radius={[8, 8, 0, 0]} />
              <Bar dataKey="attendance_rate" fill="#10b981" name="Attendance %" radius={[8, 8, 0, 0]} />
              <Bar dataKey="avg_rating" fill="#f59e0b" name="Avg Rating" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Teacher Performance (Admin Only) */}
      {isAdmin && teacherPerformance.length > 0 && (
        <Card className="p-6 bg-white border-0 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Teacher Performance
              </h3>
              <p className="text-sm text-gray-600 mt-1">Activity and engagement metrics</p>
            </div>
            <Users className="w-5 h-5 text-teal-500" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Teacher</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Center</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Batches</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Students</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Tasks</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Ratings</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Avg Rating</th>
                </tr>
              </thead>
              <tbody>
                {teacherPerformance.map((teacher, index) => (
                  <tr key={teacher.teacher_id} className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-gray-50' : ''} hover:bg-blue-50 transition-colors`}>
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-gray-900">{teacher.teacher_name}</p>
                        {teacher.is_center_head && (
                          <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700 mt-1">
                            Center Head
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{teacher.center_name}</td>
                    <td className="py-3 px-4 text-center text-sm text-gray-900">{teacher.batch_count}</td>
                    <td className="py-3 px-4 text-center text-sm text-gray-900">{teacher.student_count}</td>
                    <td className="py-3 px-4 text-center text-sm text-gray-900">{teacher.task_count}</td>
                    <td className="py-3 px-4 text-center text-sm text-gray-900">{teacher.ratings_given}</td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-900">
                        <Star className="w-3 h-3 text-yellow-500 fill-current" />
                        {teacher.avg_rating_given}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* At-Risk Students */}
      {atRiskStudents.length > 0 && (
        <Card className="p-6 bg-white border-0 shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                At-Risk Students
              </h3>
              <p className="text-sm text-gray-600 mt-1">Students requiring attention and support</p>
            </div>
            <AlertTriangle className="w-5 h-5 text-red-500" />
          </div>
          <div className="space-y-3">
            {atRiskStudents.map(student => (
              <div key={student.student_id} className="p-4 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                      <h4 className="font-semibold text-gray-900">{student.student_name}</h4>
                      {student.gk_code && (
                        <span className="text-xs text-gray-600">({student.gk_code})</span>
                      )}
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-gray-600">Attendance</p>
                        <p className="text-sm font-medium text-gray-900">{student.attendance_rate}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Task Completion</p>
                        <p className="text-sm font-medium text-gray-900">{student.task_completion_rate}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Avg Rating</p>
                        <p className="text-sm font-medium text-gray-900">{student.avg_stars}/5</p>
                      </div>
                    </div>
                    <div className="mt-3">
                      <p className="text-xs text-gray-600 mb-1">Risk Factors:</p>
                      <div className="flex flex-wrap gap-2">
                        {(student.risk_factors || []).map((factor, index) => (
                          <span key={index} className="inline-block px-2 py-1 text-xs font-medium rounded bg-red-200 text-red-800">
                            {factor}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* No At-Risk Students Message */}
      {atRiskStudents.length === 0 && (
        <Card className="p-6 bg-white border-0 shadow-lg">
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
              <Award className="w-8 h-8 text-green-600" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">All Students Performing Well!</h4>
            <p className="text-gray-600">No students currently identified as at-risk.</p>
          </div>
        </Card>
      )}
    </div>
  );
};

export default Analytics;
